/**
 * Which rendered `<p>` in a docx-preview render is the `/v1/extract` paragraph `[pN]` (MAR-1097), so
 * a review finding can scroll the viewer to its paragraph.
 *
 * WHY NOT COUNT `<p>`s. docx-editor numbers every body `w:p` in document order (table cells too),
 * then the headers', then the footers'. docx-preview renders one `<p>` per `w:p`, but repeats every
 * header and footer on every page, nests comment text in popovers, and — with changes shown — renders
 * deleted text in `<del>`. Ordinal position drifts on the first page of any real contract.
 *
 * SO MATCH BY TEXT, IN ORDER. Each body segment (in index order) takes the next rendered body
 * paragraph whose ACCEPTED text equals its own, searching a bounded window ahead. Forward-only keeps
 * repeated short texts ("1.", "Firma") in document order; the window stops one unmatched short text
 * from consuming a paragraph pages away. A segment not found is left unmapped — the caller falls back
 * to a text highlight — rather than guessed.
 *
 * A PARAGRAPH DELETED WHOLE has no accepted text; it matches on its deleted text (`originalText`),
 * which is only on the page when docx-preview renders changes. With changes hidden it stays unmapped
 * and consumes nothing — the empty `<p>` left in its place is never a match.
 */
export interface DocxSegmentText {
  index: number
  text: string
  /** Only on a paragraph deleted whole: what it said. */
  originalText?: string
}

/** A segment with no accepted text, findable only with changes shown. */
export function isWhollyDeleted(seg: DocxSegmentText): boolean {
  return !normalizeParagraphText(seg.text) && !!seg.originalText && !!normalizeParagraphText(seg.originalText)
}

/** How far past the last match a segment may be found. Generous: docx-preview renders extra `<p>`s. */
const LOOKAHEAD = 40

/** Where docx-preview puts text that is not a body paragraph. */
const NOT_BODY = 'header, footer, [class$="-comment-popover"], [class$="-comments"]'

export function normalizeParagraphText(text: string): string {
  return text.normalize('NFC').replace(/\s+/g, ' ').trim()
}

/**
 * The paragraph's accepted text: deletions, the comment marker and popovers left out. `original`
 * reads it the other way, as it was before its changes: deletions in, insertions out.
 */
export function renderedParagraphText(p: HTMLElement, read: 'accepted' | 'original' = 'accepted'): string {
  const skip = read === 'accepted' ? 'del' : 'ins'
  let text = ''
  const walk = (node: Node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.nodeValue ?? ''
        continue
      }
      if (!(child instanceof Element)) continue
      const tag = child.localName
      if (tag === skip || tag === 'p') continue
      const cls = child.getAttribute('class') ?? ''
      if (/-comment-(ref|popover)\b/.test(cls)) continue
      walk(child)
    }
  }
  walk(p)
  return normalizeParagraphText(text)
}

/** Sets `data-segment-index` on each body paragraph it can match; returns how many it matched. */
export function annotateSegments(root: HTMLElement, segments: DocxSegmentText[]): number {
  for (const el of root.querySelectorAll<HTMLElement>('[data-segment-index]')) delete el.dataset.segmentIndex

  const body = [...root.querySelectorAll<HTMLElement>('p')].filter(p => !p.closest(NOT_BODY))
  const texts = body.map(p => renderedParagraphText(p))
  let originals: string[] | null = null
  const ordered = [...segments].sort((a, b) => a.index - b.index)

  let from = 0
  let mapped = 0
  for (const seg of ordered) {
    const deleted = isWhollyDeleted(seg)
    const want = normalizeParagraphText(deleted ? seg.originalText! : seg.text)
    if (!want) continue
    if (deleted) originals ??= body.map(p => renderedParagraphText(p, 'original'))
    const end = Math.min(texts.length, from + LOOKAHEAD)
    for (let i = from; i < end; i++) {
      if (deleted ? texts[i] !== '' || originals![i] !== want : texts[i] !== want) continue
      body[i]!.dataset.segmentIndex = String(seg.index)
      from = i + 1
      mapped++
      break
    }
  }
  return mapped
}
